import * as THREE from 'three';
import { Facade } from './Facade';
import { resetMullionId } from './window/WindowHelpers';
import { addLights } from './SceneHelpers';
import { SceneCamera } from './SceneCamera';
import * as Styles from '../css/WindowDrawer.css';
import { InputHandler } from './handlers/InputHandler';
import {
  DragMode,
  InteractionHandler,
  InteractionMode,
} from './handlers/InteractionHandler';
import { MouseHandler } from './handlers/MouseHandler';
import { KeyboardHandler } from './handlers/KeyboardHandler';
import $ from 'jquery';

var Renderer = function () {
  var lastRenderTime = 0;
  var running = false;

  var camNear = 0.1;
  var camFar = 15;
  var camFov = 60.0;

  this.scene = undefined;
  this.container = undefined;
  this.renderer = undefined;
  this.camera = undefined;
  this.requireRenderUpdate = false;
  this.enableMeasurement = false;
  this.showSideText = false;
  this.enableAllMeasurements = false;
  this.showOpeningMarkers = true;
  this.flipVerticalMeasurements = false;

  this.mouseHandler = new MouseHandler(this);
  this.keyboardHandler = new KeyboardHandler(this);
  this.inputHandler = new InputHandler(this);
  this.interactionHandler = new InteractionHandler(this);

  this.definition = function () {
    return this.facade.def;
  };

  this.isWindowOpen = function () {
    throw 'Not implemented';
  };

  this.closeWindow = function (index) {
    if (index === undefined) index = 0;
    this.facade.windows[index].close();
  };

  this.openWindow = function (index) {
    if (index === undefined) index = 0;
    this.facade.windows[index].open();
  };

  this.flipWindow = function (speed, index) {
    if (index === undefined) index = 0;
    this.facade.windows[index].flip(speed);
  };

  this.zoomToFit = function (margin) {
    this.camera.zoomToFit(this.renderer, this.facade.obj, margin);
    this.requireRenderUpdate = true;
  };

  this.zoomToFitWindowIndex = function (index, margin) {
    const window = this.facade.windows[index];
    this.camera.zoomToFit(this.renderer, window.obj, margin);
    this.requireRenderUpdate = true;
  };

  this.setContainerById = function (identifier) {
    var elements = $(identifier);
    if (elements.length === 0) {
      this.container = undefined;
      return;
    }
    this.container = elements[0];
  };

  this.setContainer = function (container) {
    if (container) {
      this.container = container;
    }
  };

  this.showFrontSide = function () {
    this.camera.setCameraRotation(Math.PI);
    this.requireRenderUpdate = true;
  };

  this.showBackSide = function () {
    this.camera.setCameraRotation(0);
    this.requireRenderUpdate = true;
  };

  this.resetCameraRotation = function () {
    this.camera.setCameraRotation(0, 0);
    this.requireRenderUpdate = true;
  };

  this.showWindowIndex = function (index) {
    const window = this.facade.windows[index];
    this.camera.lookAtObject(window.obj);
    this.requireRenderUpdate = true;
  };

  this.showFacade = function () {
    this.camera.lookAtObject(this.facade.obj);
    this.requireRenderUpdate = true;
  };

  this.enableStandardMode = function () {
    this.interactionHandler.interactionMode = InteractionMode.standardMode;
    if (this.facade) {
      this.facade.rebuild();
    }
  };

  this.enableImageMode = function () {
    this.interactionHandler.interactionMode = InteractionMode.imageMode;
    if (this.facade) {
      this.facade.rebuild();
    }
  };

  this.enableEditMode = function () {
    this.interactionHandler.interactionMode = InteractionMode.editMode;
    if (this.facade) {
      this.facade.rebuild();
    }
  };

  this.enableRotateDragMode = function () {
    this.interactionHandler.dragMode = DragMode.rotate;
  };

  this.enablePanDragMode = function () {
    this.interactionHandler.dragMode = DragMode.pan;
  };

  this.setShowOpeningMarkers = function (bool) {
    if (bool) {
      this.showOpeningMarkers = true;
    } else {
      this.showOpeningMarkers = false;
    }
    if (this.facade) {
      this.facade.rebuild();
    }
  };
  this.getShowOpeningMarkers = function () {
    return this.showOpeningMarkers;
  };

  this.setShowMeasurement = function (bool) {
    if (bool) {
      this.enableMeasurement = true;
    } else {
      this.enableMeasurement = false;
      this.facade.unmeasureCurrentWindow();
      this.facade.rebuild();
    }
  };

  this.setShowSideText = function (bool) {
    if (bool) {
      this.showSideText = true;
    } else {
      this.showSideText = false;
    }
    this.facade.rebuild();
  };

  this.setEnableAllMeasurements = function (bool) {
    this.enableAllMeasurements = bool;
    if (
      (bool && !this.facade.allMeasurementsShown) ||
      (!bool && this.facade.allMeasurementsShown)
    ) {
      this.facade.toggleAllMeasurements();
    }
  };

  this.setFlipVerticalMeasurements = function (bool) {
    this.flipVerticalMeasurements = bool;
    this.facade.rebuild();
  };

  this.getShowMeasurement = function () {
    return this.enableMeasurement;
  };
  this.getShowSideText = function () {
    return this.showSideText;
  };

  this.getEnableAllMeasurement = function () {
    return this.enableAllMeasurements;
  };

  this.rotateEvent = function (bool) {
    //bool == true -> always on
    //bool == false -> always off
    //bool == undefined -> default
    if (bool === true) this.interactionHandler.enabledRotate = true;
    else if (bool === false) this.interactionHandler.enabledRotate = false;
    else this.interactionHandler.enabledRotate = undefined;
  };

  this.zoomEvent = function (bool) {
    //bool == true -> always on
    //bool == false -> always off
    //bool == undefined -> default
    if (bool === true) this.interactionHandler.enabledZoom = true;
    else if (bool === false) this.interactionHandler.enabledZoom = false;
    else this.interactionHandler.enabledZoom = undefined;
  };

  this.resize = function (e) {
    resize(e);
  };

  var reset = function () {
    resetMullionId();
    this.facade.clear();
  }.bind(this);

  this.render = function (json) {
    json = $.extend(true, {}, json);
    if (!this.container) {
      throw 'A container must be set before calling render';
    }

    this.inputHandler.closeInputs();

    if (this.renderer) {
      if (!$.contains(this.container, this.renderer.domElement)) {
        this.container.appendChild(this.renderer.domElement);
      }

      reset();
      this.facade.loadJson(json);
      this.camera.lookAtObject(this.facade.obj);
    } else {
      var cw = this.container.offsetWidth,
        ch = this.container.offsetHeight;

      this.scene = new THREE.Scene();
      this.camera = new SceneCamera(
        new THREE.PerspectiveCamera(camFov, cw / ch, camNear, camFar),
        this
      );
      this.renderer = new THREE.WebGLRenderer({
        precision: 'highp',
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
      this.renderer.setSize(cw, ch);
      this.renderer.setClearColor(0xffffff, 1);

      this.facade = new Facade(this, this.scene);

      this.container.appendChild(this.renderer.domElement);

      this.facade.loadJson(json);
      this.camera.lookAtObject(this.facade.obj);

      addLights(this.scene);

      this.mouseHandler.registerEventListeners(this.container);
      this.keyboardHandler.registerEventListeners(this.container);
      window.addEventListener('resize', resize);
    }

    lastRenderTime = 0;
    this.requireRenderUpdate = true;
    running = true;
    renderScene(0);
  };

  this.remove = function () {
    running = false;
    if (this.renderer) {
      this.mouseHandler.unregisterEventListeners(this.container);
      this.keyboardHandler.unregisterEventListeners(this.container);
      window.removeEventListener('resize', resize);
      this.renderer.dispose();
      $(this.renderer.domElement).remove();
      this.renderer = undefined;
    }
  };

  this.getBase64URL = function () {
    return this.renderer.domElement.toDataURL();
  };

  this.getSpecialSizedScreenshot = function (
    width,
    height,
    dimensions,
    showFrontSide,
    margin
  ) {
    var renderer = new THREE.WebGLRenderer({
      precision: 'highp',
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(width, height);
    renderer.setClearColor(0xffffff, 1);

    var camera;
    if (dimensions === 3) {
      camera = new SceneCamera(
        new THREE.PerspectiveCamera(camFov, width / height, camNear, camFar),
        this
      );
    } else {
      camera = new SceneCamera(
        new THREE.OrthographicCamera(
          -this.facade.width / 2 - 1 / 2000, // Adding half of a millimeter allows us to see the outer border of the window
          this.facade.width / 2 + 1 / 2000,
          this.facade.height / 2 + 1 / 2000,
          -this.facade.height / 2 - 1 / 2000,
          camNear,
          camFar
        ),
        this
      );
      //camera.obj.position.x = this.facade.width / 2;
      //camera.obj.position.y = -this.facade.height / 2;
      //camera.obj.position.z = this.facade.depth / 2 + 1;
    }
    camera.lookAtObject(this.facade.obj);

    if (showFrontSide) {
      camera.setCameraRotation(Math.PI);
    } else {
      camera.setCameraRotation(0);
    }

    camera.zoomToFit(renderer, this.facade.obj, margin);

    renderer.render(this.scene, camera.obj);
    var screenshot = renderer.domElement.toDataURL();
    return screenshot;
  };

  this.getSizedBase64URL = function () {
    var cw = this.container.offsetWidth,
      ch = this.container.offsetHeight;
    var orthoCam = new THREE.OrthographicCamera(
      -this.facade.width / 2,
      this.facade.width / 2,
      this.facade.height / 2,
      -this.facade.height / 2,
      camNear,
      camFar
    );
    orthoCam.position.x = this.facade.width / 2;
    orthoCam.position.y = -this.facade.height / 2;
    orthoCam.position.z = this.facade.depth / 2 + 1;
    this.renderer.setSize(this.facade.width * 1000, this.facade.height * 1000);
    this.renderer.render(this.scene, orthoCam);
    var screenshot = this.renderer.domElement.toDataURL();

    // Restore to original
    this.renderer.setSize(cw, ch);
    this.renderer.render(this.scene, this.camera.obj);

    return screenshot;
  };

  var update = function (deltaTime) {
    var needsUpdate = false;
    needsUpdate |= this.facade.update(deltaTime);
    return needsUpdate;
  }.bind(this);

  var renderScene = function (timestamp) {
    if (!running) {
      return;
    }

    requestAnimationFrame(renderScene);

    var deltaTime = (timestamp - lastRenderTime) / 1000;

    var needsUpdate = update(deltaTime);
    if (needsUpdate || this.requireRenderUpdate) {
      this.interactionHandler.rayTest(false);
      this.renderer.render(this.scene, this.camera.obj);
      this.inputHandler.updatePosition();
      this.requireRenderUpdate = false;
    }

    lastRenderTime = timestamp;
  }.bind(this);

  var resize = function (e) {
    if (!this.renderer) return;

    this.requireRenderUpdate = true;

    var cw = this.container.offsetWidth,
      ch = this.container.offsetHeight;
    this.camera.updateAspectRatio(cw / ch);
    this.renderer.setSize(cw, ch);

    if (this.facade) {
      this.facade.onRendererResize();
    }
  }.bind(this);
};

export { Renderer };
